/**
 * `GET /sends` (contract §5/§6) — the append-only history, newest first, including
 * `skipped_disabled` rows: a moment the policy turned off still leaves a row, so the
 * history shows the skip was deliberate rather than silent.
 */
import React, { useCallback, useEffect, useState } from "react";
import LoadFailure from "../../components/ui/LoadFailure";
import { useT, useLanguage } from "../../lib/LanguageContext";
import { formatDateTime } from "../../lib/dateTime";
import { extractApiError } from "../../lib/apiErrors";
import { LIFECYCLE } from "../../constants/testIds/lifecycle";
import lifecycleApi from "./api";
import { SENDS_LIMIT_DEFAULT } from "./contract";

const OUTCOME_CLASS = {
  sent: "text-emerald-600",
  no_transport: "text-slate-400",
  failed: "text-rose-600",
  skipped_disabled: "text-slate-400",
};

// `refreshKey` is the panel's "a pass just wrote history" signal: bumping it refetches, so
// the rows a Run now pass recorded appear without a reload.
export default function RecentSends({ api = lifecycleApi, limit = SENDS_LIMIT_DEFAULT, refreshKey = 0 }) {
  const t = useT("superadmin");
  const tv = useT("common");
  const { lang } = useLanguage();
  const [status, setStatus] = useState("loading");
  const [items, setItems] = useState([]);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const body = await api.getSends(limit);
      setItems(Array.isArray(body?.items) ? body.items : []);
      setLoadError(null);
      setStatus("ready");
    } catch (e) {
      setLoadError(e);
      setStatus("error");
    }
  }, [api, limit]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (status === "error") {
    return (
      <LoadFailure
        message={extractApiError(loadError, tv("errors.loadFailed"), tv)}
        onRetry={load}
        retryLabel={tv("actions.retry")}
        testId="lifecycle-sends-load-error"
        retryTestId="lifecycle-sends-retry"
      />
    );
  }

  return (
    <section data-testid={LIFECYCLE.sends} aria-busy={status === "loading"}>
      <h3 className="text-lg font-semibold text-slate-800 mb-3">{t("lifecycle.sends.title")}</h3>
      {items.length === 0 ? (
        <p data-testid={LIFECYCLE.sendsEmpty} className="text-slate-400 text-sm py-4 text-center">
          {t("lifecycle.sends.empty")}
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((row) => (
            <li
              key={row.send_id}
              data-testid={LIFECYCLE.sendRow(row.send_id)}
              data-outcome={row.outcome}
              className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white px-4 py-2 text-sm"
            >
              <span className="text-slate-700 truncate">{row.tenant_name}</span>
              <span className="text-slate-500 whitespace-nowrap">{t(`lifecycle.moment.${row.moment}`)}</span>
              <span className={`whitespace-nowrap ${OUTCOME_CLASS[row.outcome] || "text-slate-400"}`}>
                {t(`lifecycle.outcome.${row.outcome}`)}
              </span>
              <span className="text-slate-400 whitespace-nowrap">
                {formatDateTime(row.at, lang)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
